import { render } from 'preact';

import { namespace } from '../helpers/namespaces';
import { hexToRGB } from '../helpers/colours';
import { getTeamSelectorData, getTeamSelectorIdByMatchId } from './apiRequests';

import TeamSelectorComponent from './teamSelector.component';
import { parseThemeConfig, applyThemeFromEmbedConfig } from './theme-config';

import { determineTenant } from '../helpers/client';
import { I18nextProvider } from 'react-i18next';
import { DataCaptureForm as Form } from 'playbook-data-capture-sdk-web';
import i18nInstance from '../i18n';

import { getAuthHeader } from '../helpers/authentication';

const ID_DATA_ATTRIBUTE = `data-${namespace}team-selector`;
const ID_DATA_CAPTURE_FORM = `data-${namespace}form`;
const PREVIEW_DATA_ATTRIBUTE = `${ID_DATA_ATTRIBUTE}-preview`;
const TENANT_DATA_ATTRIBUTE = `data-${namespace}tenant`;
const INIT_ATTR = 'data-fek-initialised';
const NO_INIT_SELECTOR = `:not([${INIT_ATTR}])`;

/**
 * Class which goes through page and finds FEK predictions to embed
 */
export default class TeamSelector {
    constructor() {
        this.init();
        this.observeDOM();
    }

    teamSelectorCallback = (teamSelectorData, teamSelectorEmbed) => {
        if (!teamSelectorData) {
            return;
        }

        const authHeader = getAuthHeader();
        const teamSelectorId = teamSelectorData?.selector?.selector_id;

        if (teamSelectorData?.selector?.gated && authHeader) {
            getTeamSelectorData(teamSelectorId, true, authHeader).then(
                (gatedTeamSelectorData) => {
                    if (!gatedTeamSelectorData) {
                        return;
                    }
                    this.renderEmbed(
                        teamSelectorEmbed,
                        gatedTeamSelectorData,
                        false,
                        null
                    );
                }
            );
        } else {
            this.renderEmbed(teamSelectorEmbed, teamSelectorData, false, null);
        }
    };

    init() {
        document
            .querySelectorAll(`[${ID_DATA_ATTRIBUTE}]${NO_INIT_SELECTOR}`)
            .forEach(this.initNewTeamSelector.bind(this));
    }

    initNewTeamSelector(teamSelectorEmbed) {
        const embedTenant = teamSelectorEmbed.getAttribute(
            TENANT_DATA_ATTRIBUTE
        );
        determineTenant(embedTenant);
        const teamSelectorId =
            teamSelectorEmbed.getAttribute(ID_DATA_ATTRIBUTE);
        teamSelectorEmbed.setAttribute(INIT_ATTR, '');

        const matchId = teamSelectorEmbed.dataset.fekMatchId;
        if (teamSelectorId === 'dynamic' && matchId) {
            getTeamSelectorIdByMatchId(matchId)
                .then((data) => {
                    if (data.football_team_selector) {
                        getTeamSelectorData(data.football_team_selector).then(
                            (teamSelectorData) =>
                                this.teamSelectorCallback(
                                    teamSelectorData,
                                    teamSelectorEmbed
                                )
                        );
                    } else {
                        console.warn(
                            'Playbook Team Selector embed: No selector found for match ID',
                            matchId
                        );
                    }
                })
                .catch((error) => {
                    console.warn('Playbook Team Selector embed error', error);
                });
            return;
        }

        getTeamSelectorData(teamSelectorId).then((teamSelectorData) =>
            this.teamSelectorCallback(teamSelectorData, teamSelectorEmbed)
        );
    }

    initPreviews(onPreviewEditChange) {
        document
            .querySelectorAll(`[${PREVIEW_DATA_ATTRIBUTE}]`)
            .forEach((previewEmbed) => {
                const data = JSON.parse(
                    previewEmbed.getAttribute(PREVIEW_DATA_ATTRIBUTE)
                );
                if (data?.prediction_id) {
                    this.renderEmbed(
                        previewEmbed,
                        data,
                        true,
                        onPreviewEditChange
                    );
                }
            });
    }

    /**
     * Sets up a MutationObserver to check the DOM
     * if new score predictor embeds have been dynamically added
     */
    observeDOM() {
        const observer = new MutationObserver((mutationList) => {
            mutationList.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        const teamSelectorEmbed = node.querySelector(
                            `[${ID_DATA_ATTRIBUTE}]${NO_INIT_SELECTOR}`
                        );
                        if (teamSelectorEmbed) {
                            this.initNewTeamSelector(teamSelectorEmbed);
                        }
                    }
                });
            });
        });

        observer.observe(document.body, { subtree: true, childList: true });
    }

    /**
     * Renders embeds to DOM
     *
     * @param {Element} container - embed container
     * @param {object} teamSelectorData - teamSelectorData data
     * @param {boolean} isPreview - poll embed is a preview
     * @param {Function} onPreviewEditChange callback function when contenteditable data changes
     */
    async renderEmbed(
        container,
        teamSelectorData,
        isPreview,
        onPreviewEditChange
    ) {
        // Check if there is a linked form
        const embeddedFormId =
            container.firstElementChild?.getAttribute(ID_DATA_CAPTURE_FORM);

        let embeddedFormData;

        if (embeddedFormId) {
            embeddedFormData = await Form.initEmbeddableNewForm(embeddedFormId)
                .then((formData) => formData)
                .catch((rejectValue) => rejectValue);
        }

        // Set BG colour and gradient overrides.
        const hexBgColour = teamSelectorData?.selector?.background_colour;
        const rgbBgColour = hexBgColour ? hexToRGB(hexBgColour) : null;

        const colourOverrides = {
            hex: hexBgColour,
            rgb: rgbBgColour
        };

        // Define refetch function
        const refetch = async () => {
            const isGated = teamSelectorData?.selector?.gated;
            const authHeader = getAuthHeader();

            const latestData = await getTeamSelectorData(
                teamSelectorData?.selector?.selector_id,
                isGated,
                authHeader
            );
            if (latestData) {
                this.renderEmbed(
                    container,
                    latestData,
                    isPreview,
                    onPreviewEditChange
                );
            }
        };

        applyThemeFromEmbedConfig(container, parseThemeConfig(container));

        const shadow =
            container.shadowRoot || container.attachShadow({ mode: 'open' });
        render(
            <I18nextProvider i18n={i18nInstance}>
                <TeamSelectorComponent
                    selectorData={teamSelectorData}
                    embeddedFormData={embeddedFormData}
                    isPreview={isPreview}
                    container={container}
                    onPreviewEditChange={onPreviewEditChange}
                    colourOverrides={colourOverrides}
                    refetch={refetch}
                />
            </I18nextProvider>,
            shadow
        );
    }
}
